import { ViewCustomer } from '@xeetr/types/models'
import { SortingState } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import CustomerForm from '@/components/customers/CustomerForm'
import { getCustomerTableColumns } from '@/components/customers/customerTableColumns'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import EmptyState from '@/components/ui/empty-state'
import { useCustomerManagement } from '@/hooks/useCustomerManagement'
import { useColumnSizing } from '@/hooks/useUserSettings'
import { useColumnOrder } from '@/hooks/useColumnOrder'
import { useColumnVisibility } from '@/hooks/dashboard/useColumnVisibility'

const Customers = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<ViewCustomer | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [customerToDelete, setCustomerToDelete] = useState<ViewCustomer | null>(null)

  // Get all state from URL params (0-based page for URL display, 1-based for API)
  const currentPage = parseInt(searchParams.get('page') || '0', 10)
  const limit = parseInt(searchParams.get('limit') || '25', 10)
  const searchQuery = searchParams.get('search') || ''
  const sortBy = searchParams.get('sortBy') || 'lastName' // Default to lastName, firstName as secondary
  const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc'

  const {
    customers,
    bookingCounts,
    isFetching,
    pageCount,
    total,
    setSearchQuery,
    create,
    update,
    remove,
  } = useCustomerManagement({
    initialSearchQuery: searchQuery,
    initialPage: currentPage + 1, // Convert URL page to 1-based API page
    initialLimit: limit,
    initialSortBy: sortBy,
    initialSortOrder: sortOrder,
  })

  const handlePageChange = (pageIndex: number) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set('page', pageIndex.toString())
        return newParams
      },
      { replace: true }
    )
  }

  const handleLimitChange = (newLimit: number) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set('limit', newLimit.toString())
        newParams.set('page', '0') // Reset to first page when changing limit
        return newParams
      },
      { replace: true }
    )
  }

  const handleSearchChange = (search: string) => {
    // Only update if search actually changed
    if (search === searchQuery) {
      return
    }

    setSearchQuery(search)
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        if (search) {
          newParams.set('search', search)
        } else {
          newParams.delete('search')
        }
        // Reset page when search changes
        newParams.set('page', '0')
        return newParams
      },
      { replace: true }
    )
  }

  const handleSortingChange = (sorting: SortingState) => {
    if (sorting.length > 0) {
      const sort = sorting[0]

      // Find the column to check its sorting strategy
      const sortedColumn = columns.find((col) => {
        if ('accessorKey' in col && typeof col.accessorKey === 'string') {
          return col.accessorKey === sort.id
        }
        if ('id' in col) {
          return col.id === sort.id
        }
        return false
      })

      const sortingStrategy = sortedColumn?.meta?.sortingStrategy || 'server'

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev)
          newParams.set('sortBy', sort.id)
          newParams.set('sortOrder', sort.desc ? 'desc' : 'asc')
          // Only reset page for server-side sorts (which trigger refetch)
          if (sortingStrategy === 'server') {
            newParams.set('page', '0')
          }
          return newParams
        },
        { replace: true }
      )
    } else {
      // Clear sorting - reset to default
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev)
          newParams.delete('sortBy')
          newParams.delete('sortOrder')
          newParams.set('page', '0')
          return newParams
        },
        { replace: true }
      )
    }
  }

  const handleCreate = async (customerData: Omit<ViewCustomer, 'id' | 'tenantId'>) => {
    await create(customerData)
    setIsCreateDialogOpen(false)
  }

  const handleUpdate = async (customerData: ViewCustomer) => {
    await update(customerData)
    setEditingCustomer(null)
  }

  const handleDelete = async (customerId: string) => {
    await remove(customerId)
    setIsDeleteDialogOpen(false)
    setCustomerToDelete(null)
  }

  const handleDeleteClick = (customer: ViewCustomer) => {
    setCustomerToDelete(customer)
    setIsDeleteDialogOpen(true)
  }

  const columns = useMemo(
    () =>
      getCustomerTableColumns({
        onEdit: setEditingCustomer,
        onDelete: handleDeleteClick,
        bookingCounts,
      }),
    [bookingCounts]
  )

  const { initialColumnVisibility, handleColumnVisibilityChange } = useColumnVisibility(
    columns,
    'customer'
  )

  const { orderedColumns, updateColumnOrder } = useColumnOrder('customer')

  // Column sizing persistence
  const { columnSizing, setColumnSizing } = useColumnSizing('customers')

  // Sort customers client-side for columns with 'client' sorting strategy
  const sortedCustomers = useMemo(() => {
    // Find the column being sorted
    const sortedColumn = columns.find((col) => {
      if ('accessorKey' in col && typeof col.accessorKey === 'string') {
        return col.accessorKey === sortBy
      }
      if ('id' in col) {
        return col.id === sortBy
      }
      return false
    })

    // Check if this column uses client-side sorting
    const sortingStrategy = sortedColumn?.meta?.sortingStrategy
    if (sortingStrategy === 'client') {
      // Handle bookings column with custom logic
      if (sortBy === 'bookings') {
        const sorted = [...customers].sort((a, b) => {
          const countA = bookingCounts[a.id ?? ''] || 0
          const countB = bookingCounts[b.id ?? ''] || 0
          const comparison = countA - countB
          return sortOrder === 'desc' ? -comparison : comparison
        })
        return sorted
      }
    }
    return customers
  }, [customers, sortBy, sortOrder, bookingCounts, orderedColumns])

  return (
    <div className="px-4 py-3">
      <DataTable
        controlsLayout="compact"
        hideDefaultControls={true}
        customHeader={(controls) => (
          <div className="bg-background sticky top-0 z-10 -mx-4 border-b px-4 pb-4 pt-2">
            {/* Row 1: Search + Add Customer */}
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="max-w-sm flex-1">{controls.search}</div>
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Kunde hinzufügen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Neuen Kunden erstellen</DialogTitle>
                  </DialogHeader>
                  <CustomerForm
                    onSubmit={handleCreate}
                    onCancel={() => setIsCreateDialogOpen(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>
            {/* Row 2: Controls */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {controls.totalCount}
                <span className="text-muted-foreground">|</span>
                {controls.pageSize}
                <span className="text-muted-foreground">|</span>
                {controls.pagination}
              </div>
              <div className="flex items-center gap-2">
                {controls.columnVisibility}
                {controls.columnSettings}
              </div>
            </div>
          </div>
        )}
        columns={columns}
        columnOrder={orderedColumns}
        data={sortedCustomers}
        searchKeys={['firstName', 'lastName', 'companyName', 'phone', 'mail']}
        searchPlaceholder="Kunden nach Namen suchen..."
        initialSearchValue={searchQuery}
        serverSideSearch={true}
        onSearchChange={handleSearchChange}
        isFetching={isFetching}
        manualPagination={true}
        pageCount={pageCount}
        totalCount={total}
        serverSideSorting={true}
        alternateRowColors={true}
        initialSorting={[{ id: sortBy, desc: sortOrder === 'desc' }]}
        onSortingChange={handleSortingChange}
        onRowClick={(customer) => {
          navigate(`/customers/${customer.id}/bookings`, {
            state: {
              customer,
              searchParams: searchParams.toString(),
            },
          })
        }}
        rowClassName={() => 'cursor-pointer'}
        showColumnVisibility={true}
        showPagination={true}
        pageSize={limit}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageSizeChange={handleLimitChange}
        initialPageIndex={currentPage}
        onPageChange={handlePageChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        initialColumnVisibility={initialColumnVisibility}
        enableColumnReordering={true}
        onColumnOrderChange={updateColumnOrder}
        enableColumnResizing={true}
        initialColumnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        columnSizingStorageKey="customers-table-sizing"
        columnVisibilityButtonText="Spalten"
        columnSettingsPosition="top-right"
        emptyState={
          <EmptyState
            title="Keine Kunden gefunden"
            description="Versuchen Sie es mit einem anderen Suchbegriff oder erstellen Sie einen neuen Kunden."
          >
            <Button onClick={() => setIsCreateDialogOpen(true)} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Kunde hinzufügen
            </Button>
          </EmptyState>
        }
      />

      {/* Edit Dialog */}
      <Dialog open={!!editingCustomer} onOpenChange={() => setEditingCustomer(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kunden bearbeiten</DialogTitle>
          </DialogHeader>
          {editingCustomer && (
            <CustomerForm
              customer={editingCustomer}
              onSubmit={handleUpdate}
              onCancel={() => setEditingCustomer(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kunden löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diesen Kunden löschen möchten? Diese Aktion kann nicht
              rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCustomerToDelete(null)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (customerToDelete?.id) {
                  await handleDelete(customerToDelete.id)
                }
              }}
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Customers
